/**
 * Carriers ro'yxati (eskiz 12). Carrier'lar butun kompaniya uchun umumiy, raqamlar foydalanuvchi doirasida.
 * Owner va filial menejeri qo'shadi/tahrirlaydi, qolganlar faqat ko'radi.
 */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { cn } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { can, type Me } from "@/features/auth/types";
import { useCarriers } from "@/features/carriers/api";
import { CARRIER_STATUS_LABEL, type CarrierStatus } from "@/features/carriers/types";
import { useTranslation } from "react-i18next";
import { US_STATES } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Checkbox, FilterSelect, Input } from "@/components/ui/field";
import { StatusPill } from "@/components/ui/badge";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { EmptyState, ErrorState, PageHeader, PageLoading, SimilarNote } from "@/components/ui/misc";
import { CarrierFormModal } from "./CarrierFormModal";
import { RejectionRate, StatesChips, scopeLabel } from "./shared";
import { ContextMenu } from "@/components/ui/context-menu";
import { useCarrierActions } from "./useCarrierActions";
import { SectionSwitch } from "@/components/SectionSwitch";


export function CarriersPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Carriers me={me.data} />;
}

function Carriers({ me }: { me: Me }) {
  const carrierActions = useCarrierActions();
  const { t } = useTranslation("carriers");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const hiring = params.get("hiring") ?? "";
  const openOnly = params.get("open") === "1";
  // Truck filtri: available -- bo'sh truck'i bor (driver kerak), expiring -- truck hujjati muddati yaqin, any -- truck'i bor
  const trucks = params.get("trucks") ?? "";
  const search = params.get("search") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "carriers");
  const [createOpen, setCreateOpen] = useState(false);

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // Filtrlar va sahifalash server'da (2026-10-25: avval 100 tasi olinib brauzerda filtrlanardi -- qolganlari ko'rinmasdi)
  const carriers = useCarriers({
    page,
    page_size: pageSize,
    ordering: trucks === "available" ? "-trucks_available" : "name",
    status: status || undefined,
    search: search || undefined,
    hiring: hiring || undefined,
    open: openOnly ? 1 : undefined,
    trucks: trucks || undefined,
  });
  const rows = carriers.data?.results ?? [];
  const total = carriers.data?.count ?? 0;
  const pages = Math.max(1, carriers.data?.pages ?? 1);
  const hasFilters = !!(status || hiring || openOnly || search || trucks);

  return (
    <div>
      <PageHeader
        title={t("title")}
        subtitle={carriers.data ? t("subtitle", { count: total, scope: scopeLabel(me, t) }) : "…"}
        actions={
          <>
            <SectionSwitch ariaLabel={t("common:nav.carriers")} items={[{ to: "/carriers", label: t("common:nav.carriers") }, { to: "/jobs", label: t("common:nav.jobs") }]} />
            {can(me, "carriers_edit") && (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                {t("newCarrier")}
              </Button>
            )}
          </>
        }
      >
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <FilterSelect value={status} onChange={(e) => setParam("status", e.target.value)} className="w-auto min-w-[140px] max-w-[280px]" aria-label={t("filters.status")}>
            <option value="">{t("filters.statusAll")}</option>
            {(Object.keys(CARRIER_STATUS_LABEL) as CarrierStatus[]).map((s) => (
              <option key={s} value={s}>
                {t(`common:status.${s}`)}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={hiring} onChange={(e) => setParam("hiring", e.target.value)} className="w-auto min-w-[160px] max-w-[280px]" aria-label={t("filters.hiringState")}>
            <option value="">{t("filters.hiringAny")}</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={trucks} onChange={(e) => setParam("trucks", e.target.value)} className="w-auto min-w-[150px] max-w-[280px]" aria-label={t("filters.trucks")}>
            <option value="">{t("filters.trucksAll")}</option>
            <option value="available">{t("filters.trucksAvailable")}</option>
            <option value="expiring">{t("filters.trucksExpiring")}</option>
            <option value="any">{t("filters.trucksAny")}</option>
          </FilterSelect>
          <Checkbox label={t("filters.openOnly")} checked={openOnly} onChange={(e) => setParam("open", e.target.checked ? "1" : "")} className="ml-1" />
          <Input value={search} onChange={(e) => setParam("search", e.target.value)} placeholder={t("filters.searchPlaceholder")} className="w-[240px]" aria-label={t("filters.searchAria")} />
          {hasFilters && (
            <Button variant="link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              {t("common:actions.clearFilters")}
            </Button>
          )}
        </div>
      </PageHeader>

      {carriers.isPending ? (
        <PageLoading />
      ) : carriers.isError ? (
        <ErrorState message={carriers.error.message} onRetry={() => carriers.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={hasFilters ? t("empty.filtered") : t("empty.none")} description={can(me, "carriers_edit") ? t("empty.manager") : t("empty.viewer")} />
      ) : (
        <>
          <SimilarNote show={carriers.data?.fuzzy} />
          <Table>
            <thead>
              <tr>
                <Th>{t("columns.name")}</Th>
                <Th>{t("columns.mcDot")}</Th>
                <Th>{t("columns.status")}</Th>
                <Th>{t("columns.mainContact")}</Th>
                <Th>{t("columns.hiringStates")}</Th>
                <Th className="text-right">{t("columns.openJobs")}</Th>
                <Th className="text-right">{t("columns.freeTrucks")}</Th>
                <Th className="text-right">{t("columns.submitted")}</Th>
                <Th className="text-right">{t("columns.hired")}</Th>
                <Th className="text-right">{t("columns.rejection")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <ContextMenu key={c.id} items={() => carrierActions.items(c)}>
                  <Tr clickable onClick={() => navigate(`/carriers/${c.id}`)}>
                    <Td>
                      <Link to={`/carriers/${c.id}`} className="font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>
                        {c.name}
                      </Link>
                    </Td>
                    <Td>
                      <div className="leading-tight">
                        <div>{c.mc_number || "—"}</div>
                        <div className="text-[11px] text-muted">{c.dot_number}</div>
                      </div>
                    </Td>
                    <Td>
                      <StatusPill status={c.status} />
                    </Td>
                    <Td>
                      <div className="leading-tight">
                        <div>{c.contact_name || "—"}</div>
                        <div className="text-[11px] text-muted">{c.contact_phone}</div>
                      </div>
                    </Td>
                    <Td>
                      <StatesChips states={c.hiring_states} />
                    </Td>
                    <Td className="text-right">{c.open_jobs}</Td>
                    <Td className="text-right">
                      {/* Bo'sh truck / jami; muddati yaqin truck bo'lsa -- sariq belgi */}
                      {c.trucks_total ? (
                        <span className={cn("tabular", c.trucks_available > 0 && "font-semibold text-success")} title={c.trucks_expiring ? t("trucks.expiringCount", { count: c.trucks_expiring }) : undefined}>
                          {c.trucks_available}/{c.trucks_total}
                          {c.trucks_expiring > 0 && <span className="ml-1 text-warning">●</span>}
                        </span>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </Td>
                    <Td className="text-right">{c.submitted_count}</Td>
                    <Td className="text-right">{c.hired_count}</Td>
                    <Td className="text-right">
                      <RejectionRate rate={c.rejection_rate} />
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <Pagination
            page={page}
            pages={pages}
            count={total}
            pageSize={pageSize}
            onPage={(p) => setParam("page", String(p))}
            onPageSize={(s) => {
              rememberPageSize("carriers", s);
              setParam("page_size", String(s));
            }} label={t("pagination.carriers")} />
        </>
      )}

      {carrierActions.dialogs}
      <CarrierFormModal open={createOpen} onOpenChange={setCreateOpen} onSaved={(c) => navigate(`/carriers/${c.id}`)} />
    </div>
  );
}
